import React, { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import { formatMoney } from '../../../../utils/constants/costs'

const safeNum = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0)

const toTitle = (s: string) =>
    String(s || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (m) => m.toUpperCase())
        .trim()

const mapProviderName = (providerRaw: string, platforms: any[]) => {
    const p = String(providerRaw || '').trim()
    const low = p.toLowerCase()
    if (!low) return p
    if (low === 'openai') return 'ChatGPT'

    const list = Array.isArray(platforms) ? platforms : []
    const direct = list.find((x: any) => String(x?.label || '').toLowerCase() === low)
    if (direct?.label) return String(direct.label)

    const partial = list.find((x: any) => {
        const name = String(x?.label || '').toLowerCase()
        return name && (low.includes(name) || name.includes(low))
    })
    if (partial?.label) return String(partial.label)

    return p
}

const SectionShell = ({
    title,
    subtitle,
    right,
    children,
}: {
    title: string
    subtitle?: string
    right?: React.ReactNode
    children: React.ReactNode
}) => (
    <div className='rounded-2xl border border-gray-200 dark:border-borderColor-darkMode bg-white dark:bg-black p-4'>
        <div className='flex items-start justify-between gap-4'>
            <div className='min-w-0'>
                <p className='text-sm font-semibold text-black dark:text-white'>{title}</p>
                {subtitle ? <p className='text-xs text-gray-500 dark:text-gray-400'>{subtitle}</p> : null}
            </div>
            {right ? <div className='shrink-0'>{right}</div> : null}
        </div>
        <div className='mt-3'>{children}</div>
    </div>
)

const StatPill = ({ label, value }: { label: string; value: string }) => (
    <div className='inline-flex items-center gap-2 rounded-full border border-gray-200/70 dark:border-white/10 bg-white/70 dark:bg-white/5 px-3 py-1 text-xs text-gray-700 dark:text-gray-200 backdrop-blur'>
        <span className='text-gray-500 dark:text-gray-400'>{label}</span>
        <span className='tabular-nums font-semibold'>{value}</span>
    </div>
)

const KpiCard = ({
    title,
    value,
    sub,
}: {
    title: string
    value: string
    sub?: string
}) => (
    <div className='rounded-2xl border border-gray-200/70 dark:border-borderColor-darkMode bg-gradient-to-b from-white to-gray-50 dark:from-black dark:to-white/5 p-4 md:p-5 shadow-sm'>
        <p className='text-xs font-medium text-gray-600 dark:text-gray-300'>{title}</p>
        <p className='mt-2 text-2xl md:text-3xl font-semibold tracking-tight text-black dark:text-white tabular-nums'>{value}</p>
        {sub ? <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>{sub}</p> : null}
    </div>
)

const ClientIdentity = ({ clientDetails, totals }: { clientDetails: any; totals: any }) => {
    const client = clientDetails?.client || {}
    const name = String(client?.name || '')
    const company = String(client?.companyName || '')
    const username = String(client?.username || '')
    const email = String(client?.email || '')
    const calls = safeNum(totals?.calls)

    return (
        <div className='rounded-2xl border border-gray-200 dark:border-borderColor-darkMode bg-white dark:bg-black p-4 md:p-5'>
            <div className='flex flex-col md:flex-row md:items-start md:justify-between gap-3'>
                <div className='min-w-0'>
                    <p className='text-base md:text-lg font-semibold text-black dark:text-white truncate'>{company || name || 'Client'}</p>
                    <p className='mt-1 text-xs text-gray-500 dark:text-gray-400 truncate'>
                        {name ? `${name} · ` : ''}{username ? `${username} · ` : ''}{email}
                    </p>
                </div>
                <div className='flex flex-wrap gap-2'>
                    <StatPill label='Calls' value={calls.toLocaleString()} />
                    {/* <StatPill label='User ID' value={String(client?.userId || '')} /> */}
                </div>
            </div>
        </div>
    )
}


const buildActionSeries = (clientDetails: any) => {
    const rows = clientDetails?.breakdown?.byAction || []
    const items = rows
        .map((r: any) => {
            const actionRaw = String(r?.action || r?.key || 'unknown')
            const modeRaw = String(r?.mode || '')
            const name = modeRaw ? `${toTitle(actionRaw)} · ${toTitle(modeRaw)}` : toTitle(actionRaw)
            return {
                key: `${actionRaw}__${modeRaw}`,
                name,
                usd: safeNum(r?.totalUsd),
                calls: safeNum(r?.calls),
                action: actionRaw,
                mode: modeRaw,
            }
        })
        .filter((x: any) => x.usd > 0 || x.calls > 0)
        .sort((a: any, b: any) => b.usd - a.usd)

    const total = items.reduce((a: number, x: any) => a + x.usd, 0)
    return { items, total }
}

const buildActionDonut = (clientDetails: any, themeMode: 'dark' | 'light') => {
    const { items, total } = buildActionSeries(clientDetails)

    const maxSlices = 6
    const head = items.slice(0, maxSlices)
    const tail = items.slice(maxSlices)
    const otherUsd = tail.reduce((a: number, x: any) => a + x.usd, 0)

    const data = [
        ...head.map((x: any) => ({ name: x.name, value: x.usd, calls: x.calls })),
        ...(otherUsd > 0
            ? [
                {
                    name: 'Other',
                    value: otherUsd,
                    calls: tail.reduce((a: number, x: any) => a + x.calls, 0),
                },
            ]
            : []),
    ].filter((x: any) => safeNum(x.value) > 0)

    return {
        tooltip: {
            trigger: 'item',
            formatter: (p: any) => {
                const v = safeNum(p?.value)
                const pct = total > 0 ? (v / total) * 100 : 0
                const calls = safeNum(p?.data?.calls)
                return [
                    `<div style='font-weight:800;margin-bottom:6px'>${String(p?.name || '')}</div>`,
                    `<div>Cost: <b>$${formatMoney(v)}</b> <span style='color:#6b7280'>(${pct.toFixed(1)}%)</span></div>`,
                    `<div>Calls: <b>${calls.toLocaleString()}</b></div>`,
                ].join('')
            },
        },
        legend: {
            bottom: 0,
            icon: 'circle',
            itemWidth: 8,
            itemHeight: 8,
            textStyle: { color: '#6b7280' },
        },
        series: [
            {
                type: 'pie',
                radius: ['58%', '78%'],
                center: ['50%', '42%'],
                minShowLabelAngle: 10,
                avoidLabelOverlap: true,
                label: {
                    show: true,
                    color: '#6b7280',
                    formatter: (p: any) => {
                        const pct = safeNum(p?.percent)
                        if (pct < 8) return ''
                        return `${p.name}\n$${formatMoney(safeNum(p.value))}`
                    },
                },
                labelLine: {
                    length: 10,
                    length2: 10,
                    lineStyle: { color: 'rgba(156,163,175,0.35)' },
                },
                data,
            },
        ],
        graphic: [
            {
                type: 'text',
                left: 'center',
                top: '31%',
                style: {
                    text: 'Total',
                    fill: themeMode === 'dark' ? '#fff' : '#6b7280',
                    fontSize: 12,
                    fontWeight: 700,
                },
            },
            {
                type: 'text',
                left: 'center',
                top: '40%',
                style: {
                    text: `$${formatMoney(total)}`,
                    fill: themeMode === 'dark' ? '#fff' : '#111827',
                    fontSize: 16,
                    fontWeight: 900,
                },
            },
        ],
    }
}

const buildActionBars = (clientDetails: any, themeMode: 'dark' | 'light') => {
    const { items, total } = buildActionSeries(clientDetails)
    const top = items.slice(0, 10)

    const labels = top.map((x: any) => x.name)
    const values = top.map((x: any) => x.usd)

    return {
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params: any) => {
                const p = Array.isArray(params) ? params[0] : params
                const idx = safeNum(p?.dataIndex)
                const row = top[idx]
                if (!row) return ''
                const pct = total > 0 ? (row.usd / total) * 100 : 0
                return [
                    `<div style='font-weight:800;margin-bottom:6px'>${row.name}</div>`,
                    `<div>Cost: <b>$${formatMoney(row.usd)}</b> <span style='color:#6b7280'>(${pct.toFixed(1)}%)</span></div>`,
                    `<div>Calls: <b>${row.calls.toLocaleString()}</b></div>`,
                ].join('')
            },
        },
        grid: { left: 10, right: 18, top: 10, bottom: 10, containLabel: true },
        xAxis: {
            type: 'value',
            axisLabel: { show: false },
            axisTick: { show: false },
            axisLine: { show: false },
            splitLine: { show: false },
        },
        yAxis: {
            type: 'category',
            data: labels,
            axisTick: { show: false },
            axisLine: { show: false },
            axisLabel: {
                color: themeMode === 'dark' ? '#fff' : '#111827',
                fontWeight: 700,
                width: 210,
                overflow: 'truncate',
            },
        },
        series: [
            {
                type: 'bar',
                data: values,
                barWidth: 18,
                itemStyle: { borderRadius: 8 },
                label: {
                    show: true,
                    position: 'right',
                    color: '#6b7280',
                    formatter: (p: any) => `$${formatMoney(safeNum(p.value))}`,
                },
            },
        ],
    }
}

const buildProviderBars = (clientDetails: any, themeMode: 'dark' | 'light', platforms: any[]) => {
    const rows = clientDetails?.breakdown?.byProvider || []
    const mapped = rows
        .map((r: any) => {
            const raw = String(r?.provider || 'unknown')
            return {
                provider: mapProviderName(raw, platforms),
                usd: safeNum(r?.totalUsd),
                calls: safeNum(r?.calls),
            }
        })
        .filter((x: any) => x.usd > 0 || x.calls > 0)

    const providers = mapped.map((d: any) => d.provider)
    const usd = mapped.map((d: any) => d.usd)
    const calls = mapped.map((d: any) => d.calls)

    return {
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params: any) => {
                const items = Array.isArray(params) ? params : [params]
                const name = String(items?.[0]?.name || '')
                const u = safeNum(items?.find((x: any) => x.seriesName === 'Cost')?.value)
                const c = safeNum(items?.find((x: any) => x.seriesName === 'Calls')?.value)
                return [
                    `<div style='font-weight:800;margin-bottom:6px'>${name}</div>`,
                    `<div>Cost: <b>$${formatMoney(u)}</b></div>`,
                    `<div>Calls: <b>${c.toLocaleString()}</b></div>`,
                ].join('')
            },
        },
        legend: {
            top: 0,
            icon: 'circle',
            itemWidth: 8,
            itemHeight: 8,
            textStyle: { color: '#6b7280' },
        },
        grid: { left: 10, right: 10, top: 34, bottom: 10, containLabel: true },
        xAxis: {
            type: 'value',
            axisLabel: { color: '#6b7280' },
            splitLine: { lineStyle: { color: 'rgba(156,163,175,0.18)' } },
        },
        yAxis: {
            type: 'category',
            data: providers,
            axisTick: { show: false },
            axisLine: { show: false },
            axisLabel: { color: themeMode === 'dark' ? '#fff' : '#111827', fontWeight: 700 },
        },
        series: [
            { name: 'Cost', type: 'bar', barWidth: 18, data: usd, itemStyle: { borderRadius: 8 } },
            { name: 'Calls', type: 'bar', barWidth: 18, data: calls, itemStyle: { borderRadius: 8, opacity: 0.55 } },
        ],
    }
}

const buildPropertyScatter = (clientDetails: any, themeMode: 'dark' | 'light') => {
    const rows = clientDetails?.breakdown?.byProperty || []
    const points = rows
        .map((r: any) => {
            const name = String(r?.name || 'Unknown property')
            const city = String(r?.city || '')
            const country = String(r?.country || '')
            const label = [name, city, country].filter(Boolean).join(' · ')
            const usd = safeNum(r?.totalUsd)
            const calls = safeNum(r?.calls)
            return {
                name: label,
                value: [usd, calls],
                usd,
                calls,
                action: String(r?.action || ''),
                mode: String(r?.mode || ''),
            }
        })
        .filter((p: any) => p.usd > 0 || p.calls > 0)

    const maxCalls = Math.max(...points.map((p: any) => p.calls), 1)

    return {
        tooltip: {
            formatter: (p: any) => {
                const usd = safeNum(p?.data?.usd)
                const calls = safeNum(p?.data?.calls)
                const action = String(p?.data?.action || '')
                const mode = String(p?.data?.mode || '')
                return [
                    `<div style='font-weight:800;margin-bottom:6px'>${String(p?.name || '')}</div>`,
                    `<div>Cost: <b>$${formatMoney(usd)}</b></div>`,
                    `<div>Calls: <b>${calls.toLocaleString()}</b></div>`,
                    action ? `<div>Action: <b>${action}</b></div>` : '',
                    mode ? `<div>Mode: <b>${mode}</b></div>` : '',
                ].filter(Boolean).join('')
            },
        },
        grid: { left: 12, right: 12, top: 10, bottom: 14, containLabel: true },
        xAxis: {
            type: 'value',
            axisLabel: { color: '#6b7280', formatter: (v: number) => `$${formatMoney(safeNum(v))}` },
            splitLine: { lineStyle: { color: 'rgba(156,163,175,0.18)' } },
        },
        yAxis: {
            type: 'value',
            axisLabel: { color: '#6b7280' },
            splitLine: { lineStyle: { color: 'rgba(156,163,175,0.18)' } },
        },
        series: [
            {
                type: 'scatter',
                symbolSize: (val: any) => {
                    const c = safeNum(val?.[1])
                    const t = Math.min(1, c / maxCalls)
                    return 10 + t * 26
                },
                data: points,
                itemStyle: {
                    opacity: 0.85,
                    borderColor: themeMode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(17,24,39,0.12)',
                    borderWidth: 1,
                },
            },
        ],
    }
}

const normalizeBreakdown = (stats: any) => {
    const breakdown = Array.isArray(stats?.breakdown) ? stats.breakdown : []
    const totals = stats?.totals || {}
    return {
        totals: {
            totalUsd: safeNum(totals?.totalUsd),
            calls: safeNum(totals?.calls),
        },
        breakdown: breakdown.map((x: any) => ({
            key: String(x?.key || ''),
            label: String(x?.label || x?.key || ''),
            totalUsd: safeNum(x?.totalUsd),
            calls: safeNum(x?.calls),
        })),
    }
}

const BreakdownList = ({ stats }: { stats: any }) => {
    const ui = useMemo(() => normalizeBreakdown(stats), [stats])
    const rows = ui.breakdown.slice().sort((a: any, b: any) => b.totalUsd - a.totalUsd)
    const maxV = Math.max(...rows.map((r: any) => r.totalUsd), 0)

    return (
        <div className='space-y-3'>
            {rows.map((r: any) => (
                <div key={r.key}>
                    <div className='flex items-center justify-between gap-3'>
                        <p className='text-sm text-gray-700 dark:text-gray-200 truncate'>{r.label}</p>
                        <p className='text-xs text-gray-500 dark:text-gray-400 tabular-nums whitespace-nowrap'>
                            ${formatMoney(r.totalUsd)} · {r.calls.toLocaleString()} {r.calls === 1 ? 'call' : 'calls'}
                        </p>
                    </div>
                    <div className='mt-2 h-2 w-full rounded-full bg-gray-200/70 dark:bg-white/10 overflow-hidden'>
                        <div
                            className='h-full rounded-full bg-black/70 dark:bg-white/70'
                            style={{ width: `${Math.max(2, maxV > 0 ? (r.totalUsd / maxV) * 100 : 0)}%` }}
                        />
                    </div>
                </div>
            ))}
        </div>
    )
}

const ClientCostsDashboard = ({
    mode,
    loading,
    clientDetails,
    stats,
    totals,
    platforms,
}: {
    mode: 'dark' | 'light'
    loading?: boolean
    clientDetails?: any
    stats?: any
    totals?: any
    platforms?: any[]
}) => {

    const kpis = useMemo(() => {
        const t = totals || {}
        const totalUsd = safeNum(t?.totalUsd)
        const calls = safeNum(t?.calls)
        const req = safeNum(t?.requests)
        const inTok = safeNum(t?.inputTokens)
        const outTok = safeNum(t?.outputTokens)
        const costPerCall = calls > 0 ? totalUsd / calls : 0
        const costPer1kIn = inTok > 0 ? (totalUsd / inTok) * 1000 : 0
        return { totalUsd, calls, req, inTok, outTok, costPerCall, costPer1kIn }
    }, [totals])

    const actionMeta = useMemo(() => buildActionSeries(clientDetails), [clientDetails])
    const actionBarsHeight = useMemo(() => {
        const n = Math.min(10, actionMeta.items.length || 0)
        return Math.max(220, n * 54)
    }, [actionMeta.items.length])

    const actionDonutOption = useMemo(() => buildActionDonut(clientDetails, mode), [clientDetails, mode])
    const actionBarsOption = useMemo(() => buildActionBars(clientDetails, mode), [clientDetails, mode])
    const providerOption = useMemo(() => buildProviderBars(clientDetails, mode, platforms || []), [clientDetails, mode, platforms])
    const propertyOption = useMemo(() => buildPropertyScatter(clientDetails, mode), [clientDetails, mode])

    if (loading) {
        return (
            <div className='grid grid-cols-1 gap-4'>
                <div className='rounded-2xl border border-gray-200 dark:border-borderColor-darkMode bg-white/60 dark:bg-white/5 p-5 animate-pulse'>
                    <div className='h-4 w-48 rounded bg-gray-200 dark:bg-gray-700' />
                    <div className='mt-4 h-3 w-72 rounded bg-gray-200 dark:bg-gray-700' />
                </div>
                <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4'>
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className='rounded-2xl border border-gray-200 dark:border-borderColor-darkMode bg-white/60 dark:bg-white/5 p-5 animate-pulse'>
                            <div className='h-3 w-24 rounded bg-gray-200 dark:bg-gray-700' />
                            <div className='mt-4 h-8 w-28 rounded bg-gray-200 dark:bg-gray-700' />
                        </div>
                    ))}
                </div>
                <div className='grid grid-cols-1 xl:grid-cols-12 gap-4'>
                    <div className='xl:col-span-7 rounded-2xl border border-gray-200 dark:border-borderColor-darkMode bg-white/60 dark:bg-white/5 p-5 animate-pulse' style={{ height: 360 }} />
                    <div className='xl:col-span-5 rounded-2xl border border-gray-200 dark:border-borderColor-darkMode bg-white/60 dark:bg-white/5 p-5 animate-pulse' style={{ height: 360 }} />
                </div>
            </div>
        )
    }

    return (
        <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
                <ClientIdentity clientDetails={clientDetails} totals={totals} />
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4'>
                <KpiCard title='Total cost' value={`$${formatMoney(kpis.totalUsd)}`} sub={`${kpis.calls.toLocaleString()} calls`} />
                <KpiCard title='Cost per call' value={`$${formatMoney(kpis.costPerCall)}`} sub='Blended across actions' />
                <KpiCard title='Input tokens' value={kpis.inTok.toLocaleString()} sub={`Output: ${kpis.outTok.toLocaleString()}`} />
                <KpiCard title='Cost per 1k input' value={`$${formatMoney(kpis.costPer1kIn)}`} sub='Heuristic efficiency' />
            </div>
            <div className='grid grid-cols-1 gap-4 items-start'>
                <div className=''>
                    <SectionShell title='Stats breakdown' subtitle='Matches backend breakdown keys'>
                        <BreakdownList stats={stats} />
                    </SectionShell>
                </div>
            </div>
            <div className='grid grid-cols-1 xl:grid-cols-12 gap-4'>
                <div className='xl:col-span-7'>
                    <SectionShell
                        title='Where the cost goes'
                        subtitle='Top actions'
                        right={<StatPill label='Total' value={`$${formatMoney(kpis.totalUsd)}`} />}
                    >
                        <div className='grid grid-cols-1 lg:grid-cols-1 gap-3'>
                            <div className='rounded-xl border border-gray-200/70 dark:border-white/10 bg-white/70 dark:bg-white/5 p-3'>
                                <ReactECharts option={actionDonutOption} style={{ height: 300, width: '100%' }} notMerge lazyUpdate />
                            </div>
                            <div className='rounded-xl border border-gray-200/70 dark:border-white/10 bg-white/70 dark:bg-white/5 p-3'>
                                <ReactECharts option={actionBarsOption} style={{ height: actionBarsHeight, width: '100%' }} notMerge lazyUpdate />
                            </div>
                        </div>
                    </SectionShell>
                </div>

                <div className='xl:col-span-5 flex flex-col gap-4'>
                    <SectionShell title='Providers' subtitle='Cost and calls by provider'>
                        <ReactECharts option={providerOption} style={{ height: 220, width: '100%' }} notMerge lazyUpdate />
                    </SectionShell>

                    <SectionShell title='Properties' subtitle='Bubble size reflects calls, x is cost'>
                        <ReactECharts option={propertyOption} style={{ height: 220, width: '100%' }} notMerge lazyUpdate />
                    </SectionShell>
                </div>
            </div>
        </div>
    )
}

export default ClientCostsDashboard;